import React, { useState } from "react";
import { ArrowRight, Bell, Headphones, MessageCircle, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { track } from "@/lib/analytics";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "../WaitlistForm";

const HERO_IMAGE =
  "https://i.ibb.co/prGPHcG7/Botella-sin-fondo.png";

export default function Hero() {
  const { t } = useLang();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const chips = t("hero.chips");
  const strip = t("hero.strip");

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-cream-100"
    >
      <div className="absolute inset-0 lg:left-1/2 bg-cream-200">
        <img
          src={HERO_IMAGE}
          alt={t("hero.image_alt")}
          className="h-full w-full object-contain object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-cream-100 via-cream-100/50 to-transparent lg:bg-gradient-to-r lg:from-cream-100 lg:via-cream-100/10 lg:to-transparent" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-36 sm:pt-44 pb-24 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center min-h-[100vh]">
        <div className="max-w-xl animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="divider-hairline" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
              {t("hero.overline")}
            </span>
          </div>

          <h1 className="font-serif text-[38px] sm:text-6xl lg:text-[76px] leading-[1.12] sm:leading-[1.05] lg:leading-[1.02] tracking-tight text-olive-900 font-medium">
            {t("hero.h1_a")}
            <span className="italic text-olive-700"> {t("hero.h1_b")}</span>
            {/* Preserve the visual line break but keep textContent readable
                for SEO crawlers (H1 audit was reading "temprananacido"). */}
            <br />
            {" " + t("hero.h1_c")}
            <span className="italic"> {t("hero.h1_d")}</span>.
          </h1>

          <p className="mt-4 text-sm sm:text-base text-olive-800/65 font-light uppercase tracking-[0.18em]">
            AOVE premium · Limones, Moclín · Granada
          </p>

          <p className="mt-8 text-base sm:text-lg leading-relaxed text-olive-800/80 font-light max-w-lg">
            {t("hero.body")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            {PRE_HARVEST_MODE ? (
              <button
                type="button"
                onClick={() => { track("cta_click", { cta: "hero_waitlist", location: "home" }); setShowWaitlist(true); }}
                data-testid="hero-cta-button"
                className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-all duration-300 group"
              >
                <Bell size={14} strokeWidth={1.4} />
                {t("hero.cta_primary")}
              </button>
            ) : (
              <a
                href="/tienda"
                data-testid="hero-cta-button"
                onClick={() => track("cta_click", { cta: "hero_buy_primary", location: "home" })}
                className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-all duration-300 group"
              >
                {t("hero.cta_primary")}
                <ArrowRight size={16} strokeWidth={1.2} className="transition-transform group-hover:translate-x-1" />
              </a>
            )}
            <a
              href="#historia"
              data-testid="hero-secondary-button"
              onClick={() => track("cta_click", { cta: "hero_discover_estate", location: "home" })}
              className="inline-flex items-center gap-3 px-8 py-4 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:bg-cream-200 transition-colors duration-300"
            >
              {t("hero.cta_secondary")}
            </a>
            <a
              href="https://wa.me/34626411029?text=Hola%2C%20me%20interesa%20el%20AOVE%20de%20Cortijo%20Santa%20Mar%C3%ADa"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              onClick={() => track("cta_click", { cta: "hero_whatsapp", location: "home" })}
              className="inline-flex items-center gap-3 px-8 py-4 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-700 hover:text-cream-100 transition-colors duration-300"
            >
              <MessageCircle size={16} strokeWidth={1.4} />
              {t("hero.cta_whatsapp")}
            </a>
          </div>

          {/* Heritage / craft line — replaces the previous scarcity counter */}
          <p
            data-testid="hero-scarcity"
            className="mt-5 inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.28em] text-olive-900"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-deep opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-deep" />
            </span>
            {t("hero.scarcity_suffix")}
          </p>

          {/* Trust / reassurance row: clarifies what happens after the click */}
          <ul
            data-testid="hero-trust-row"
            className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.2em] text-olive-800/65"
          >
            <li className="inline-flex items-center gap-2">
              <ShieldCheck size={13} strokeWidth={1.3} className="text-gold-deep" />
              {t("hero.trust_payment") || "Pago seguro"}
            </li>
            <li className="inline-flex items-center gap-2">
              <Bell size={13} strokeWidth={1.3} className="text-gold-deep" />
              {t("hero.trust_shipping")}
            </li>
            <li className="inline-flex items-center gap-2">
              <Headphones size={13} strokeWidth={1.3} className="text-gold-deep" />
              {t("hero.trust_support") || "Atención directa del cortijo"}
            </li>
          </ul>

          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-[11px] uppercase tracking-[0.24em] text-olive-800/70">
            {chips.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>

        <div className="hidden lg:flex justify-end items-end relative h-full" />
      </div>

      <div className="relative z-10 border-t border-cream-400 bg-cream-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex flex-wrap justify-between gap-4 text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-olive-800/70">
          {strip.map((s, i) => (
            <span
              key={s}
              className={i === 1 ? "hidden sm:inline" : i === 3 ? "hidden md:inline" : ""}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
      {showWaitlist && <WaitlistForm onClose={() => setShowWaitlist(false)} />}
    </section>
  );
}
